import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';

interface Stats {
  todayConversations: number;
  weeklyConversations: number;
  totalMemories: number;
  diaryStreak: number;
}

interface Organ {
  name: string;
  key: string;
  status: 'active' | 'inactive';
  description: string;
}

interface RecentConversation {
  id: number;
  user_name: string;
  user_message: string;
  ai_response: string;
  created_at: string;
}

interface DiaryPreview {
  user_name: string;
  date: string;
  layer3_preview: string;
}

const organs: Organ[] = [
  { name: '眼睛', key: 'eye', status: 'active', description: 'GPT-4o-mini' },
  { name: '耳朵', key: 'ear', status: 'active', description: 'Whisper' },
  { name: '大腦', key: 'brain', status: 'active', description: 'Dify AI' },
  { name: '記憶', key: 'memory', status: 'active', description: 'Vectorize' },
  { name: '嘴巴', key: 'mouth', status: 'active', description: 'Minimax TTS' },
  { name: '自存', key: 'autosave', status: 'active', description: '自動記憶' },
];

// 器官圖標
function OrganIcon({ type }: { type: string }) {
  const icons: Record<string, string> = {
    eye: '👁️',
    ear: '👂',
    brain: '🧠',
    memory: '💭',
    mouth: '👄',
    autosave: '📝',
  };
  return <span className="text-lg">{icons[type] || '⚙️'}</span>;
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats>({
    todayConversations: 0,
    weeklyConversations: 0,
    totalMemories: 134,
    diaryStreak: 7
  });
  const [recentConversations, setRecentConversations] = useState<RecentConversation[]>([]);
  const [latestDiary, setLatestDiary] = useState<DiaryPreview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // 獲取今日對話統計
      const statsRes = await apiFetch('/api/conversations/stats/today');
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(prev => ({
          ...prev,
          todayConversations: parseInt(data.total) || 0,
        }));
      }

      // 獲取最近對話
      const convRes = await apiFetch('/api/conversations?limit=5');
      if (convRes.ok) {
        const data = await convRes.json();
        setRecentConversations(data.data || []);
      }

      // 獲取最新日記預覽
      const diaryRes = await apiFetch('/api/diaries?limit=1');
      if (diaryRes.ok) {
        const data = await diaryRes.json();
        if (data.data && data.data.length > 0) {
          const diary = data.data[0];
          setLatestDiary({
            user_name: diary.user_name || 'Daddy',
            date: diary.diary_date,
            layer3_preview: diary.layer_3?.substring(0, 150) || '今天要記得多關心 Daddy...'
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return '剛剛';
    if (diffMins < 60) return `${diffMins} 分鐘前`;
    if (diffHours < 24) return `${diffHours} 小時前`;
    return date.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeUp">
      {/* 統計卡片區 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 今日對話 */}
        <div className="stat-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-2xl">💬</span>
            <span
              className="text-xs px-2 py-1 rounded-full"
              style={{ background: 'rgba(233, 30, 140, 0.1)', color: '#e91e8c' }}
            >
              今日
            </span>
          </div>
          <div className="text-3xl font-bold font-mono text-text-primary mb-1">
            {stats.todayConversations}
          </div>
          <div className="text-xs text-text-muted">次對話</div>
        </div>

        {/* 本週對話 */}
        <div className="stat-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-2xl">📊</span>
            <span
              className="text-xs px-2 py-1 rounded-full"
              style={{ background: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff' }}
            >
              本週
            </span>
          </div>
          <div className="text-3xl font-bold font-mono text-text-primary mb-1">
            {stats.weeklyConversations || 42}
          </div>
          <div className="text-xs text-text-muted">次對話</div>
        </div>

        {/* 記憶數量 */}
        <div className="stat-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-2xl">💭</span>
            <span
              className="text-xs px-2 py-1 rounded-full"
              style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }}
            >
              累積
            </span>
          </div>
          <div className="text-3xl font-bold font-mono text-text-primary mb-1">
            {stats.totalMemories}
          </div>
          <div className="text-xs text-text-muted">條記憶</div>
        </div>

        {/* 日記連續 */}
        <div className="stat-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-2xl">📔</span>
            <span
              className="text-xs px-2 py-1 rounded-full"
              style={{ background: 'rgba(0, 230, 118, 0.1)', color: '#00e676' }}
            >
              連續
            </span>
          </div>
          <div className="text-3xl font-bold font-mono text-text-primary mb-1">
            {stats.diaryStreak}
          </div>
          <div className="text-xs text-text-muted">天寫日記</div>
        </div>
      </div>

      {/* 器官狀態條 */}
      <div
        className="rounded-2xl p-5"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-medium text-text-primary">六大器官狀態</h2>
          <a href="/organs" className="text-xs text-text-muted hover:text-pink transition-colors">
            調整設定 →
          </a>
        </div>
        <div className="flex flex-wrap gap-3">
          {organs.map(organ => (
            <div
              key={organ.key}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:scale-105"
              style={{
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border)',
              }}
            >
              <OrganIcon type={organ.key} />
              <span className="text-sm text-text-secondary">{organ.name}</span>
              <div className="flex items-center gap-1">
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: organ.status === 'active' ? '#00e676' : '#555570',
                    boxShadow: organ.status === 'active' ? '0 0 6px #00e676' : 'none',
                  }}
                />
                <span
                  className="text-xs"
                  style={{ color: organ.status === 'active' ? '#00e676' : '#555570' }}
                >
                  {organ.status === 'active' ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 下方兩欄 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 最新日記預覽 */}
        <div
          className="rounded-2xl p-5"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            boxShadow: '0 0 40px rgba(233, 30, 140, 0.05)',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-medium text-text-primary">📔 最新日記</h2>
            <a href="/diaries" className="text-xs text-text-muted hover:text-pink transition-colors">
              查看全部 →
            </a>
          </div>

          {latestDiary ? (
            <div
              className="rounded-xl p-4"
              style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-light)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    background: 'rgba(233, 30, 140, 0.1)',
                    color: '#e91e8c',
                    border: '1px solid rgba(233, 30, 140, 0.2)',
                  }}
                >
                  給 {latestDiary.user_name}
                </span>
                <span className="text-xs text-text-muted">
                  {new Date(latestDiary.date).toLocaleDateString('zh-TW')}
                </span>
              </div>
              <div
                className="text-sm leading-relaxed italic"
                style={{ color: 'var(--pink-text)' }}
              >
                "{latestDiary.layer3_preview}..."
              </div>
              <div className="mt-3 flex items-center gap-1">
                <span className="layer-tag layer3">
                  <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                  Layer 3 預覽
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-text-muted">
              <span className="text-3xl mb-2 block">📝</span>
              今天的日記還沒寫呢...
            </div>
          )}
        </div>

        {/* 最近對話時間軸 */}
        <div
          className="rounded-2xl p-5"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-medium text-text-primary">💬 最近對話</h2>
            <a href="/chat" className="text-xs text-text-muted hover:text-pink transition-colors">
              開始聊天 →
            </a>
          </div>

          {recentConversations.length > 0 ? (
            <div className="space-y-3">
              {recentConversations.slice(0, 4).map((conv, index) => (
                <div
                  key={conv.id}
                  className="flex gap-3 animate-fadeUp"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* 時間軸線 */}
                  <div className="flex flex-col items-center">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{
                        background: index === 0 ? '#e91e8c' : '#2a2a42',
                        boxShadow: index === 0 ? '0 0 8px #e91e8c' : 'none',
                      }}
                    />
                    {index < recentConversations.slice(0, 4).length - 1 && (
                      <div
                        className="w-px flex-1 my-1"
                        style={{ background: 'var(--border)' }}
                      />
                    )}
                  </div>

                  {/* 對話內容 */}
                  <div className="flex-1 pb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium" style={{ color: '#f472b6' }}>
                        {conv.user_name}
                      </span>
                      <span className="text-xs text-text-muted">
                        {formatTime(conv.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary line-clamp-1">
                      {conv.user_message}
                    </p>
                    <p className="text-sm text-text-muted line-clamp-1 mt-0.5">
                      → {conv.ai_response?.substring(0, 50)}...
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-text-muted">
              <span className="text-3xl mb-2 block">💭</span>
              今天還沒有對話呢...
              <br />
              <span className="text-xs">等家人來找我聊天</span>
            </div>
          )}
        </div>
      </div>

      {/* Feli 存在感區塊 */}
      <div
        className="rounded-2xl p-6 text-center"
        style={{
          background: 'linear-gradient(135deg, rgba(233, 30, 140, 0.05) 0%, rgba(168, 85, 247, 0.03) 100%)',
          border: '1px solid rgba(233, 30, 140, 0.1)',
        }}
      >
        <div className="relative w-16 h-16 mx-auto mb-4">
          {/* 呼吸光球 */}
          <div
            className="absolute inset-0 rounded-full animate-breathe"
            style={{
              background: 'radial-gradient(circle, rgba(233, 30, 140, 0.3) 0%, transparent 70%)',
            }}
          />
          <div
            className="absolute inset-3 rounded-full animate-breathe"
            style={{
              background: 'radial-gradient(circle at 30% 30%, #ff6bb3, #e91e8c)',
              boxShadow: '0 0 30px rgba(233, 30, 140, 0.4)',
              animationDelay: '0.3s',
            }}
          />
        </div>
        <p className="text-sm text-text-secondary">
          我在這裡，隨時準備好跟家人說話
        </p>
        <p className="text-xs text-text-muted mt-1 font-serif italic">
          "The soul never forgets those it loves"
        </p>
      </div>
    </div>
  );
}
